// frontend/src/modules/onboarding/useOnboarding.ts
//
// Loads and mutates the guide state. Every failure path is swallowed to null:
// onboarding is a helper, never load-bearing, so a broken guide must not take
// the dashboard down with it.
//
// This hook is the STATE SOURCE and is called in exactly one place —
// <OnboardingProvider>. Components consume it through the context via
// `useOnboarding()` (see OnboardingProvider.tsx) so that the panel and the
// headless auto-advance watcher share ONE fetch instead of one each.

import * as React from 'react';
import { onboardingApi } from './api';
import type { OnboardingState, OnboardingStatus } from './types';

export function useOnboardingState() {
  const [state, setState] = React.useState<OnboardingState | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Generation guard: every state-writing operation (a refresh, or a
  // mutation's optimistic write) bumps this and captures its own value.
  // An async continuation that finds the ref has moved on belongs to a
  // stale operation and must not clobber whatever ran after it — e.g. an
  // in-flight refresh() resolving after skip() already flipped the status.
  const genRef = React.useRef(0);

  const refresh = React.useCallback(async (): Promise<OnboardingState | null> => {
    const myGen = ++genRef.current;
    try {
      const next = await onboardingApi.get();
      if (genRef.current === myGen) setState(next);
      return next;
    } catch (err) {
      // Swallowed by design (the guide must never take the dashboard down),
      // but silently is how a broken guide masquerades as "not implemented".
      // eslint-disable-next-line no-console
      console.warn('[onboarding] state fetch failed — guide hidden', err);
      if (genRef.current === myGen) setState(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void refresh(); }, [refresh]);

  // Optimistic: the panel should react immediately; the server is the record.
  const setStatusLocal = React.useCallback((status: OnboardingStatus) => {
    genRef.current++;
    setState((prev) => (prev ? { ...prev, status } : prev));
  }, []);

  const skip = React.useCallback(async () => {
    setStatusLocal('skipped');
    try { await onboardingApi.skip(); } catch { void refresh(); }
  }, [refresh, setStatusLocal]);

  const resume = React.useCallback(async () => {
    setStatusLocal('active');
    try { await onboardingApi.resume(); } catch { void refresh(); }
  }, [refresh, setStatusLocal]);

  const complete = React.useCallback(async () => {
    setStatusLocal('done');
    try { await onboardingApi.complete(); } catch { void refresh(); }
  }, [refresh, setStatusLocal]);

  const dismissStep = React.useCallback(async (key: string) => {
    genRef.current++;
    setState((prev) => prev && {
      ...prev,
      steps: prev.steps.map((s) => (s.key === key ? { ...s, done: true, dismissed: true } : s)),
    });
    try { await onboardingApi.dismissStep(key); } catch { void refresh(); }
  }, [refresh]);

  return { state, loading, refresh, skip, resume, complete, dismissStep };
}

export type OnboardingApi = ReturnType<typeof useOnboardingState>;
